import React, { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom';

import _ from 'lodash';

import useGetEmploymentIndustries from '../../../../hooks/misc/useGetEmploymentIndustries';
import useGetLocations from '../../../../hooks/locations/useGetLocations';
import useGetSchools from '../../../../hooks/misc/useGetSchools';
import useGetJobPostDetails from '../../../../hooks/job_posts/useGetJobPostDetails';
import useSaveJobPost from '../../../../hooks/job_posts/useSaveJobPost';

import MainLayout from '../../../layouts/Main';
import JobDetailsForm from '../../../sub_components/job_portals/JobDetails';
import JobApplicationDetailsForm from '../../../sub_components/job_portals/JobApplicationDetails';
import JobAudienceForm from '../../../sub_components/job_portals/JobAudience';
import JobApplicantsList from '../../../sub_components/tables/JobApplicantsList';
import useGetAIRecommendations_JobTitles from '../../../../hooks/misc/useGetAIRecommendations_JobTitles';

import { IJobPostFormData, JobPostDetailsTab, JobPostStatus, TRequiredJobPostFormField } from '../../../../interfaces/job_posts';
import { ISortQuery, IAttachedFileWithUrl, IJobTitle } from '../../../../interfaces/services';

import { OPEmploymentTypes, OPJobLevels, OPOnsiteRemote, OPSalaryCurrencies } from '../../../../data/options';

import { getDateString } from '../../../../helpers/utility';
import useGetJobApplicationsByJobPost from '../../../../hooks/misc/useGetJobApplicationsByJobPost';
import { SVGIcon } from '../../../wrappers/SVGIcon';
import { Icons } from '../../../../data/Assets';

const JobDetailsPage = () => {
    const { job_post_id } = useParams();
    const { state } = useLocation();

    const requiredFields: TRequiredJobPostFormField[] = ['title', 'description', 'status'];

    const tabsList: JobPostDetailsTab[] = Object.values(JobPostDetailsTab);

    const initialFormData: IJobPostFormData = {
        id: null,
        location_id: { value: { value: '', label: '' }, error: '' },
        title: { value: '', error: '' },
        occupation: { value: { value: '', label: '' }, error: '' },
        open_positions: { value: '', error: '' },
        description: { value: '', error: '' },
        industry_id: { value: { value: '', label: '' }, error: '' },
        onsite_remote: { value: { value: '', label: '' }, error: '' },
        employment_type: { value: { value: '', label: '' }, error: '' },
        responsibilities: { value: '', error: '' },
        qualifications: { value: '', error: '' },
        job_level: { value: { value: '', label: '' }, error: '' },
        salary_min: { value: '', error: '' },
        salary_max: { value: '', error: '' },
        salary_currency: { value: { value: '', label: '' }, error: '' },
        salary_period: { value: { value: '', label: '' }, error: '' },
        experience_level: { value: '', error: '' },
        keywords: { value: '', error: '' },
        advert_start: { value: '', error: '' },
        advert_end: { value: '', error: '' },
        job_start: { value: '', error: '' },
        require_cover_letter: { value: { value: '', label: '' }, error: '' },
        contact_title: { value: '', error: '' },
        contact_email: { value: '', error: '' },
        web_link: { value: '', error: '' },
        associated_schools: { value: [], error: '' },
    }

    const [currentTab, setCurrentTab] = useState<JobPostDetailsTab>(tabsList[0]);
    const [attachedFiles, setAttachedFiles] = useState<IAttachedFileWithUrl[]>([]);
    const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [occupationRecommendations, setOccupationRecommendations] = useState<IJobTitle[]>([]);
    const [isOccupationRecommendationsLoading, setIsOccupationRecommendationsLoading] = useState<boolean>(false);
    const [originalFormData, setOriginalFormData] = useState<IJobPostFormData>(initialFormData);
    const [formData, setFormData] = useState<IJobPostFormData>(initialFormData);
    const [isFormInitialized, setIsFormInitialized] = useState<boolean>(false);

    const sortQuery: ISortQuery = { sort_key: 'identifier', sort_order: 'ASC' };

    const { data: jobPostDetails, error: jobPostDetailsError } = useGetJobPostDetails(Number(job_post_id));
    const { data: employmentIndustries, isLoading: isEmploymentIndustriesLoading, error: employmentIndustriesError } = useGetEmploymentIndustries();
    const { data: locations, isLoading: isLocationsLoading, error: locationsError } = useGetLocations('organization', undefined, sortQuery);
    const { data: schools, isLoading: isSchoolsLoading, error: schoolsError } = useGetSchools();
    const { data: jobApplications, isLoading: isJobApplicationsLoading } = useGetJobApplicationsByJobPost(Number(job_post_id), !!job_post_id && currentTab === JobPostDetailsTab.APPLICANTS);

    const { mutate: saveJobPost } = useSaveJobPost();
    const { mutate: getAIRecommendationsJobTitles } = useGetAIRecommendations_JobTitles();

    const error = jobPostDetailsError || employmentIndustriesError || locationsError || schoolsError;

    const renderTabContent = () => {
        switch (currentTab) {
            case JobPostDetailsTab.JOB_DETAILS:
                return <JobDetailsForm
                    formData={formData}
                    isFormDisabled={isFormDisabled}
                    attachedFiles={attachedFiles}
                    filesToUpload={filesToUpload}
                    setAttachedFiles={setAttachedFiles}
                    setFilesToUpload={setFilesToUpload}
                    setFormData={setFormData}
                    occupationRecommendations={occupationRecommendations}
                    isOccupationRecommendationsLoading={isOccupationRecommendationsLoading}
                    employmentIndustries={employmentIndustries || []}
                    locations={locations || []}
                    isLoading={isLocationsLoading || isEmploymentIndustriesLoading}
                />;
            case JobPostDetailsTab.APPLICATION_DETAILS:
                return <JobApplicationDetailsForm
                    formData={formData}
                    isFormDisabled={isFormDisabled}
                    setFormData={setFormData}
                />;
            case JobPostDetailsTab.AUDIENCE:
                return <JobAudienceForm
                    formData={formData}
                    isFormDisabled={isFormDisabled}
                    setFormData={setFormData}
                    schools={schools || []}
                    isSchoolsLoading={isSchoolsLoading}
                />;
            case JobPostDetailsTab.APPLICANTS:
                return <JobApplicantsList isLoading={isJobApplicationsLoading} jobApplications={jobApplications || []} />
        }
    }

    const getOccupationRecommendations = (query: string, limit: number, delay: number, callback: (data: IJobTitle[]) => void) => {
        if (!query.trim().length) {
            callback([]);
            return;
        }

        const debouncedGetOccupationRecommendations = _.debounce(() => {
            getAIRecommendationsJobTitles({
                setIsProcessing: setIsOccupationRecommendationsLoading,
                payload: { query, limit },
                successCallback: callback,
            });
        }, delay);

        debouncedGetOccupationRecommendations();
    }

    const initializeFormData = () => {
        if (!jobPostDetails || !locations || !employmentIndustries || !schools) return null;

        const selectedLocation = locations.find(location => location.id === jobPostDetails.location_id);
        const selectedIndustry = employmentIndustries.find(industry => industry.id === jobPostDetails.industry_id);
        const selectedJobType = OPEmploymentTypes.find(jobType => jobType.value === jobPostDetails.employment_type);
        const selectedOnsiteRemote = OPOnsiteRemote.find(onsiteRemote => onsiteRemote.value === jobPostDetails.onsite_remote);
        const selectedJobLevel = OPJobLevels.find(jobLevel => jobLevel.value === jobPostDetails.job_level);
        const selectedSalaryCurrency = OPSalaryCurrencies.find(salaryCurrency => salaryCurrency.value === jobPostDetails.salary_currency);
        const selectedAdvertStartDate = jobPostDetails.advert_start ? getDateString(jobPostDetails.advert_start) : '';
        const selectedAdvertEndDate = jobPostDetails.advert_end ? getDateString(jobPostDetails.advert_end) : '';
        const selectedJobStartDate = jobPostDetails.job_start ? getDateString(jobPostDetails.job_start) : '';
        const selectedRequireCoverLetter = jobPostDetails.require_cover_letter === true ? 'true' : 'false';

        const selectedAssociatedSchools = jobPostDetails.associated_schools.map(school => schools.find(s => s.id === school.school_id));

        const locationLabel = selectedLocation ? `${selectedLocation.identifier} - ${selectedLocation.employer.legal_name}` : '';
        const industryLabel = selectedIndustry ? `${selectedIndustry.name}` : '';
        const jobTypeLabel = selectedJobType ? `${selectedJobType.label}` : '';
        const onsiteRemoteLabel = selectedOnsiteRemote ? `${selectedOnsiteRemote.label}` : '';
        const jobLevelLabel = selectedJobLevel ? `${selectedJobLevel.label}` : '';
        const salaryCurrencyLabel = selectedSalaryCurrency ? `${selectedSalaryCurrency.label}` : '';

        const formData: IJobPostFormData = {
            id: jobPostDetails.id,
            location_id: { value: { value: selectedLocation?.id?.toString() ?? '', label: locationLabel }, error: '' },
            open_positions: { value: jobPostDetails.open_positions?.toString() ?? '', error: '' },
            title: { value: jobPostDetails.title, error: '' },
            occupation: { value: { value: '', label: '' }, error: '' },
            description: { value: jobPostDetails.description, error: '' },
            industry_id: { value: { value: selectedIndustry?.id?.toString() ?? '', label: industryLabel }, error: '' },
            onsite_remote: { value: { value: selectedOnsiteRemote?.value ?? '', label: onsiteRemoteLabel }, error: '' },
            employment_type: { value: { value: selectedJobType?.value ?? '', label: jobTypeLabel }, error: '' },
            responsibilities: { value: jobPostDetails.responsibilities ?? '', error: '' },
            qualifications: { value: jobPostDetails.qualifications ?? '', error: '' },
            job_level: { value: { value: selectedJobLevel?.value ?? '', label: jobLevelLabel }, error: '' },
            salary_min: { value: jobPostDetails.salary_min ? `${jobPostDetails.salary_min}` : '', error: '' },
            salary_max: { value: jobPostDetails.salary_max ? `${jobPostDetails.salary_max}` : '', error: '' },
            salary_currency: { value: { value: selectedSalaryCurrency?.value ?? '', label: salaryCurrencyLabel }, error: '' },
            keywords: { value: jobPostDetails.keywords ?? '', error: '' },
            advert_start: { value: selectedAdvertStartDate ? `${selectedAdvertStartDate}` : '', error: '' },
            advert_end: { value: selectedAdvertEndDate ? `${selectedAdvertEndDate}` : '', error: '' },
            job_start: { value: selectedJobStartDate ? `${selectedJobStartDate}` : '', error: '' },
            require_cover_letter: { value: { value: selectedRequireCoverLetter, label: selectedRequireCoverLetter === 'true' ? 'Yes' : 'No' }, error: '' },
            contact_title: { value: jobPostDetails.contact_title ?? '', error: '' },
            contact_email: { value: jobPostDetails.contact_email ?? '', error: '' },
            web_link: { value: jobPostDetails.web_link ?? '', error: '' },
            associated_schools: { value: selectedAssociatedSchools.map(school => ({ value: school?.id?.toString() ?? '', label: school?.name ?? '' })), error: '' },
            salary_period: { value: { value: jobPostDetails.salary_period ?? '', label: jobPostDetails.salary_period ?? '' }, error: '' },
            experience_level: { value: jobPostDetails.experience_level ?? '', error: '' },
        }

        setAttachedFiles(jobPostDetails.attached_files);
        setFilesToUpload([]);
        setFormData(formData);
        setOriginalFormData(formData);
        setIsFormInitialized(true);
    }

    const checkRequiredFields = () => {
        type TFormDataKeys = keyof IJobPostFormData;
        let isValid = true;
        let newFormData = { ...formData };

        requiredFields.forEach(field => {
            const formField = formData[field as TFormDataKeys];
            if (formField && typeof formField === 'object' && 'value' in formField && 'error' in formField) {
                const isEmpty = typeof formField.value === 'string'
                    ? formField.value.trim() === ''
                    : typeof formField.value === 'object' && !Array.isArray(formField.value) && (!formField.value || !('value' in formField.value));

                if (isEmpty) {
                    isValid = false;
                }
                (newFormData[field as TFormDataKeys] as { error: string }).error = isEmpty ? 'This field is required' : '';
            }
        });

        setFormData(newFormData);
        return isValid;
    }

    const isDataSame = () => {
        return _.isEqual(originalFormData, formData) && attachedFiles.length === jobPostDetails?.attached_files.length && filesToUpload.length === 0;
    }

    const isOkToSave = () => {
        return checkRequiredFields() && !isProcessing && !isDataSame();
    }

    const submitJobPost = (type: JobPostStatus) => {
        const multiPartFormData = new FormData();

        const jobStartDate = formData.job_start.value ? getDateString(formData.job_start.value, 'yyyy-mm-dd') : null;
        const advertStartDate = formData.advert_start.value ? getDateString(formData.advert_start.value, 'yyyy-mm-dd') : null;
        const advertEndDate = formData.advert_end.value ? getDateString(formData.advert_end.value, 'yyyy-mm-dd') : null;

        multiPartFormData.append('id', jobPostDetails?.id?.toString() || '');
        multiPartFormData.append('status', type);
        multiPartFormData.append('title', formData.title.value || '');
        multiPartFormData.append('open_positions', formData.open_positions.value || '');
        multiPartFormData.append('occupation', formData.occupation.value.value || '');
        multiPartFormData.append('description', formData.description.value || '');
        multiPartFormData.append('responsibilities', formData.responsibilities.value || '');
        multiPartFormData.append('qualifications', formData.qualifications.value || '');
        multiPartFormData.append('location_id', formData.location_id.value.value || '');
        multiPartFormData.append('industry_id', formData.industry_id.value.value || '');
        multiPartFormData.append('onsite_remote', formData.onsite_remote.value.value || '');
        multiPartFormData.append('employment_type', formData.employment_type.value.value || '');
        multiPartFormData.append('job_level', formData.job_level.value.value || '');
        multiPartFormData.append('salary_min', formData.salary_min.value || '');
        multiPartFormData.append('salary_max', formData.salary_max.value || '');
        multiPartFormData.append('salary_currency', formData.salary_currency.value.value || '');
        multiPartFormData.append('salary_period', formData.salary_period.value.value || '');
        multiPartFormData.append('experience_level', formData.experience_level.value || '');
        multiPartFormData.append('keywords', formData.keywords.value || '');
        multiPartFormData.append('advert_start', advertStartDate || '');
        multiPartFormData.append('advert_end', advertEndDate || '');
        multiPartFormData.append('job_start', jobStartDate || '');
        multiPartFormData.append('require_cover_letter', formData.require_cover_letter.value.value || '');
        multiPartFormData.append('contact_title', formData.contact_title.value || '');
        multiPartFormData.append('contact_email', formData.contact_email.value || '');
        multiPartFormData.append('web_link', formData.web_link.value || '');
        formData.associated_schools.value.forEach(school => {
            multiPartFormData.append('associated_school_ids', school.value);
        });

        filesToUpload.forEach(file => {
            multiPartFormData.append('files', file);
        });

        attachedFiles.forEach(file => {
            multiPartFormData.append('attached_file_ids', file.id.toString());
        });

        saveJobPost({
            setIsProcessing,
            setErrorMessage,
            payload: multiPartFormData,
        });
    }

    const handleSaveDraft = () => {
        if (jobPostDetails?.status !== JobPostStatus.DRAFT) {
            return;
        }

        if (isOkToSave()) {
            submitJobPost(JobPostStatus.DRAFT);
        }
    }

    const handlePost = () => {
        if (isOkToSave() || jobPostDetails?.status !== JobPostStatus.ACTIVE) {
            submitJobPost(JobPostStatus.ACTIVE);
        }
    }

    useEffect(() => {
        if (errorMessage) {
            setErrorMessage(null);
        }
    }, [formData, errorMessage]);

    useEffect(() => {
        initializeFormData();
    }, [jobPostDetails, locations, employmentIndustries, schools]); // eslint-disable-line

    useEffect(() => {
        if (jobPostDetails?.occupation && isFormInitialized) {
            getOccupationRecommendations(
                jobPostDetails.occupation,
                10,
                0,
                (data: IJobTitle[]) => {
                    const selectedOccupation = data.find(occupation => occupation.job_title === jobPostDetails.occupation);

                    if (selectedOccupation) {
                        const occupation = { value: selectedOccupation.job_title, label: selectedOccupation.noc_code.noc_code + ' - ' + selectedOccupation.job_title };
                        setFormData({ ...formData, occupation: { value: occupation, error: '' } });
                        setOriginalFormData({ ...originalFormData, occupation: { value: occupation, error: '' } });
                    }

                    setOccupationRecommendations(data);
                },
            );
        }
    }, [jobPostDetails, isFormInitialized]); // eslint-disable-line

    useEffect(() => {
        if (formData.title.value) {
            getOccupationRecommendations(
                formData.title.value,
                10,
                2000,
                (data: IJobTitle[]) => {
                    setOccupationRecommendations(data)
                }
            );
        }
    }, [formData.title.value]); // eslint-disable-line

    const isDisableButtons = isDataSame() || isProcessing;
    const isJobPostActive = jobPostDetails?.status === JobPostStatus.ACTIVE;
    const isDraftDisabled = !!jobPostDetails?.id && isJobPostActive;
    const isFormDisabled = jobPostDetails?.status === JobPostStatus.ARCHIVED;


    const isPostButtonDisabled = () => {
        if ((isJobPostActive && isDataSame()) || isProcessing || isFormDisabled) {
            return true;
        }

        return false;
    }

    const isSaveDraftButtonDisabled = () => {
        if (isDraftDisabled || isFormDisabled || isDisableButtons) {
            return true;
        }

        return false;
    }

    useEffect(() => {
        if (state?.currentTab) {
            setCurrentTab(state.currentTab);
        }
    }, [state]);

    return (
        <MainLayout>
            <div className="immployer__common_details__layout">
                <div className="immployer__form_details__head">
                    <h2 className="color_dark">{jobPostDetails?.title}</h2>

                    <div className="immployer__form_details__head__actions">
                        <button
                            className={`immployer__btn immployer__btn__secondary ${isSaveDraftButtonDisabled() ? 'immployer__btn__secondary__disabled' : ''}`}
                            onClick={handleSaveDraft}
                            disabled={isSaveDraftButtonDisabled()}
                        >
                            Save Draft
                        </button>
                        <button
                            className={`immployer__btn immployer__btn__primary ${isPostButtonDisabled() ? 'immployer__btn__primary__disabled' : ''}`}
                            onClick={handlePost}
                            disabled={isPostButtonDisabled()}
                        >
                            {isJobPostActive ? 'Update Post' : 'Post'}
                        </button>
                    </div>
                </div>

                <div className='immployer__form_details__sub_head'>
                    <div className='immployer__form_details__sub_head__item'>
                        <SVGIcon src={Icons.Calendar} color='dark_grey' />
                        <span>Posted on {jobPostDetails?.created_at ? getDateString(jobPostDetails?.created_at) : ''}</span>
                    </div>

                    <div className='immployer__form_details__sub_head__item color_primary'>
                        <SVGIcon src={Icons.CheckCircle3Quarters} color='dark_grey' />
                        <span>{jobPostDetails?.applicants} Application(s)</span>
                    </div>

                    <div className='immployer__form_details__sub_head__item color_primary'>
                        <SVGIcon src={Icons.EyeFill} color='dark_grey' />
                        <span>{jobPostDetails?.job_views} View(s)</span>
                    </div>
                </div>

                <div className="immployer__common_details__tabs__container">
                    <div className="immployer__tabs">
                        {tabsList.map((tab, index) =>
                            <button
                                key={index}
                                className={`immployer__tab ${currentTab === tab ? "immployer__tab__active" : ""}`}
                                onClick={() => setCurrentTab(tab)}
                            >
                                {tab}
                            </button>
                        )}
                    </div>
                </div>
                <div className="immployer__common_details__content">
                    {
                        error
                            ? <div className="immployer__common_details__error_view">
                                <br /><br />
                                <h4 className="color_dark text_center">Error occured while fetching the data.</h4>
                                <h4 className="color_dark text_center">Please try again later.</h4>
                            </div>
                            : renderTabContent()
                    }
                </div>
            </div>
        </MainLayout>
    )
}

export default JobDetailsPage;